import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createRoom,
  getRooms,
  getRoom,
  updateRoom,
  deleteRoom,
  getActiveRoomsCount,
  saveRoomWeeklyAvailability,
  saveRoomScheduleSettings,
  getRoomWeeklyAvailability,
  getRoomAvailabilityForRange,
  resolveRoomAvailabilityForRange,
  deleteOldRoomAvailability,
  checkRoomAvailability,
  getAvailableRoomsForSlot,
  getRoomAppointments,
  getWeekId,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from "../../firebase";

export const roomsApi = createApi({
  reducerPath: "roomsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Rooms", "Room", "RoomAvailability", "RoomAppointments"],
  endpoints: (builder) => ({
    // ============================================
    // ROOM CRUD OPERATIONS
    // ============================================

    // Get all rooms for a clinic
    getRooms: builder.query({
      async queryFn({ clinicId }) {
        try {
          const rooms = await getRooms(clinicId);
          return { data: rooms };
        } catch (error) {
          console.error("[CK-9100] Error fetching rooms:", {
            errorCode: "CK-9100",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Rooms", id })),
              { type: "Rooms", id: "LIST" },
            ]
          : [{ type: "Rooms", id: "LIST" }],
    }),

    // Get a single room
    getRoom: builder.query({
      async queryFn({ clinicId, roomId }) {
        try {
          const room = await getRoom(clinicId, roomId);
          if (!room) {
            return { error: { message: "Room not found" } };
          }
          return { data: room };
        } catch (error) {
          console.error("[CK-9101] Error fetching room:", {
            errorCode: "CK-9101",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomId }) => [{ type: "Room", id: roomId }],
    }),

    // Create a new room
    createRoom: builder.mutation({
      async queryFn({ clinicId, roomData, therapistId, therapistName }) {
        try {
          const room = await createRoom(clinicId, roomData);

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.CREATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: room.id,
            entityName: roomData.name,
            details: {
              capacity: roomData.capacity,
              isAccessible: roomData.isAccessible,
              defaultTherapistId: roomData.defaultTherapistId,
              allowOthersToBook: roomData.allowOthersToBook,
            },
            success: true,
          });

          return { data: room };
        } catch (error) {
          console.error("[CK-9102] Error creating room:", {
            errorCode: "CK-9102",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.CREATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityName: roomData?.name,
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Rooms", id: "LIST" }],
    }),

    // Update a room
    updateRoom: builder.mutation({
      async queryFn({ clinicId, roomId, roomData, therapistId, therapistName }) {
        try {
          const room = await updateRoom(clinicId, roomId, roomData);

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            entityName: roomData.name || room.name,
            details: {
              updatedFields: Object.keys(roomData),
            },
            success: true,
          });

          return { data: room };
        } catch (error) {
          console.error("[CK-9103] Error updating room:", {
            errorCode: "CK-9103",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { roomId }) => [
        { type: "Rooms", id: "LIST" },
        { type: "Room", id: roomId },
      ],
    }),

    // Delete a room
    deleteRoom: builder.mutation({
      async queryFn({ clinicId, roomId, roomName, therapistId, therapistName }) {
        try {
          await deleteRoom(clinicId, roomId);

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.DELETE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            entityName: roomName,
            success: true,
          });

          return { data: roomId };
        } catch (error) {
          console.error("[CK-9104] Error deleting room:", {
            errorCode: "CK-9104",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.DELETE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            entityName: roomName,
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Rooms", id: "LIST" }],
    }),

    // Get active rooms count (for checking if rooms feature is enabled)
    getActiveRoomsCount: builder.query({
      async queryFn({ clinicId }) {
        try {
          const count = await getActiveRoomsCount(clinicId);
          return { data: count };
        } catch (error) {
          console.error("[CK-9105] Error getting active rooms count:", {
            errorCode: "CK-9105",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Rooms", id: "COUNT" }],
    }),

    // ============================================
    // ROOM AVAILABILITY OPERATIONS
    // ============================================

    // Get weekly availability for a room
    getRoomWeeklyAvailability: builder.query({
      async queryFn({ clinicId, roomId, weekId }) {
        try {
          const availability = await getRoomWeeklyAvailability(
            clinicId,
            roomId,
            weekId
          );
          return { data: availability };
        } catch (error) {
          console.error("[CK-9110] Error fetching room weekly availability:", {
            errorCode: "CK-9110",
            errorMessage: error.message,
            clinicId,
            roomId,
            weekId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomId, weekId }) => [
        { type: "RoomAvailability", id: `${roomId}-${weekId}` },
      ],
    }),

    // Get room availability for date range
    getRoomAvailabilityForRange: builder.query({
      async queryFn({ clinicId, roomId, startDate, endDate }) {
        try {
          const slots = await getRoomAvailabilityForRange(
            clinicId,
            roomId,
            new Date(startDate),
            new Date(endDate)
          );
          return { data: slots };
        } catch (error) {
          console.error("[CK-9111] Error fetching room availability for range:", {
            errorCode: "CK-9111",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomId }) => [
        { type: "RoomAvailability", id: `${roomId}-RANGE` },
      ],
    }),

    // Save weekly availability (per-date exceptions) for a room
    saveRoomWeeklyAvailability: builder.mutation({
      async queryFn({ clinicId, roomId, weekId, slots }) {
        try {
          const result = await saveRoomWeeklyAvailability(
            clinicId,
            roomId,
            weekId,
            slots
          );

          // Clean up past weeks in the background — never block the save on it
          const currentWeekId = getWeekId();
          deleteOldRoomAvailability(clinicId, roomId, currentWeekId).catch(
            () => {}
          );

          return { data: result };
        } catch (error) {
          console.error("[CK-9112] Error saving room weekly availability:", {
            errorCode: "CK-9112",
            errorMessage: error.message,
            clinicId,
            roomId,
            weekId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { roomId, weekId }) => [
        { type: "RoomAvailability", id: `${roomId}-${weekId}` },
        { type: "RoomAvailability", id: `${roomId}-RANGE` },
      ],
    }),

    // Save recurring schedule settings on the room doc
    // (scheduleTemplate / alwaysAvailable / blockOnHolidays)
    saveRoomScheduleSettings: builder.mutation({
      async queryFn({ clinicId, roomId, settings, therapistId, therapistName }) {
        try {
          const room = await saveRoomScheduleSettings(clinicId, roomId, settings);

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            entityName: room?.name,
            details: { updatedFields: Object.keys(settings || {}) },
            success: true,
          });

          return { data: room };
        } catch (error) {
          console.error("[CK-9113] Error saving room schedule settings:", {
            errorCode: "CK-9113",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_ROOM,
            entityType: ENTITY_TYPES.ROOM,
            entityId: roomId,
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { roomId }) => [
        { type: "Rooms", id: "LIST" },
        { type: "Room", id: roomId },
        { type: "RoomAvailability", id: `${roomId}-RANGE` },
      ],
    }),

    // Resolved per-day open windows for a date range (calendar overlay)
    getRoomResolvedAvailabilityForRange: builder.query({
      async queryFn({ clinicId, roomId, startDate, endDate }) {
        try {
          const days = await resolveRoomAvailabilityForRange(
            clinicId,
            roomId,
            new Date(startDate),
            new Date(endDate)
          );
          return { data: days };
        } catch (error) {
          console.error("[CK-9114] Error resolving room availability range:", {
            errorCode: "CK-9114",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomId }) => [
        { type: "RoomAvailability", id: `${roomId}-RANGE` },
        { type: "Room", id: roomId },
      ],
    }),

    // Resolve availability for SEVERAL rooms in one call (rooms calendar).
    // Returns { [roomId]: [{ date, mode, slots }] }. A single room failing
    // fails the whole query so the grid never silently drops a room's shading.
    getRoomsResolvedAvailabilityForRange: builder.query({
      async queryFn({ clinicId, roomIds = [], startDate, endDate }) {
        try {
          const entries = await Promise.all(
            roomIds.map(async (roomId) => [
              roomId,
              await resolveRoomAvailabilityForRange(
                clinicId,
                roomId,
                new Date(startDate),
                new Date(endDate)
              ),
            ])
          );
          return { data: Object.fromEntries(entries) };
        } catch (error) {
          console.error("[CK-9123] Error resolving rooms availability range:", {
            errorCode: "CK-9123",
            errorMessage: error.message,
            clinicId,
            roomsCount: roomIds.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomIds = [] }) =>
        roomIds.flatMap((roomId) => [
          { type: "RoomAvailability", id: `${roomId}-RANGE` },
          { type: "Room", id: roomId },
        ]),
    }),

    // ============================================
    // ROOM BOOKING UTILITIES
    // ============================================

    // Check if a room is available at a specific time
    checkRoomAvailability: builder.query({
      async queryFn({
        clinicId,
        roomId,
        startTime,
        endTime,
        excludeAppointmentId,
      }) {
        try {
          const result = await checkRoomAvailability(
            clinicId,
            roomId,
            new Date(startTime),
            new Date(endTime),
            excludeAppointmentId
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9120] Error checking room availability:", {
            errorCode: "CK-9120",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Don't cache - always fetch fresh data
      keepUnusedDataFor: 0,
    }),

    // Get available rooms for a time slot
    getAvailableRoomsForSlot: builder.query({
      async queryFn({
        clinicId,
        startTime,
        endTime,
        therapistId,
        excludeAppointmentId,
      }) {
        try {
          const result = await getAvailableRoomsForSlot(
            clinicId,
            new Date(startTime),
            new Date(endTime),
            therapistId,
            excludeAppointmentId
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9121] Error getting available rooms for slot:", {
            errorCode: "CK-9121",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Don't cache - always fetch fresh data
      keepUnusedDataFor: 0,
    }),

    // Get room appointments for calendar view
    getRoomAppointments: builder.query({
      async queryFn({ clinicId, roomId, startDate, endDate }) {
        try {
          const appointments = await getRoomAppointments(
            clinicId,
            roomId,
            new Date(startDate),
            new Date(endDate)
          );
          return { data: appointments };
        } catch (error) {
          console.error("[CK-9122] Error fetching room appointments:", {
            errorCode: "CK-9122",
            errorMessage: error.message,
            clinicId,
            roomId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { roomId }) => [
        { type: "RoomAppointments", id: roomId },
        { type: "RoomAppointments", id: "LIST" },
      ],
    }),
  }),
});

export const {
  // Room CRUD
  useGetRoomsQuery,
  useGetRoomQuery,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
  useGetActiveRoomsCountQuery,
  // Room Availability
  useGetRoomWeeklyAvailabilityQuery,
  useGetRoomAvailabilityForRangeQuery,
  useGetRoomResolvedAvailabilityForRangeQuery,
  useGetRoomsResolvedAvailabilityForRangeQuery,
  useSaveRoomWeeklyAvailabilityMutation,
  useSaveRoomScheduleSettingsMutation,
  // Room Booking
  useCheckRoomAvailabilityQuery,
  useGetAvailableRoomsForSlotQuery,
  useGetRoomAppointmentsQuery,
} = roomsApi;
